import React from 'react';
import { useParams } from 'react-router-dom';
import NavigationBar from '../components/Navbar';
import Footer from '../components/Footer';
import { LanguageContext } from '../contexts/LanguageContext';
import { Helmet } from 'react-helmet';
import EditableText from '../components/EditableText';
import { plantIconMap } from '../components/PlantIcons';

/**
 * Plant configuration with EditableText IDs starting at 10000+ to avoid collisions.
 *
 * ID scheme (4 IDs per plant):
 *   DE headline (= tooltip text) | EN headline (= tooltip text) | DE body | EN body
 *
 * The headline ID is shared between the SVG tooltip and this page heading.
 */
interface PlantConfig {
  de: {
    title: string;
    headlineId: string;   // same ID used for SVG tooltip (DE)
    textId: string;
    metaDesc: string;
  };
  en: {
    title: string;
    headlineId: string;   // same ID used for SVG tooltip (EN)
    textId: string;
    metaDesc: string;
  };
}

export const plantConfigs: Record<string, PlantConfig> = {
  'pfanne': {
    de: {
      title: 'Pfanne',
      headlineId: '10000',
      textId: '10002',
      metaDesc: 'Die Pfanne ist ein zentrales Element im Schmelzbetrieb. meltshop|365 unterstützt die digitale Verwaltung und Überwachung Ihrer Pfannen.',
    },
    en: {
      title: 'Ladle',
      headlineId: '10001',
      textId: '10003',
      metaDesc: 'The ladle is a central element in melt shop operations. meltshop|365 supports the digital management and monitoring of your ladles.',
    },
  },
  'kran-verwiegung': {
    de: {
      title: 'Kran / Verwiegung',
      headlineId: '10004',
      textId: '10006',
      metaDesc: 'Kran- und Verwiegungssysteme im Schmelzbetrieb. meltshop|365 ermöglicht die präzise Erfassung und Steuerung von Kran- und Wiegeprozessen.',
    },
    en: {
      title: 'Crane / Weighing',
      headlineId: '10005',
      textId: '10007',
      metaDesc: 'Crane and weighing systems in melt shop operations. meltshop|365 enables precise recording and control of crane and weighing processes.',
    },
  },
  'bof-konverter': {
    de: {
      title: 'BOF / Konverter',
      headlineId: '10008',
      textId: '10010',
      metaDesc: 'Der BOF / Konverter im Schmelzbetrieb. meltshop|365 unterstützt die Prozessautomatisierung und Datenerfassung am Konverter.',
    },
    en: {
      title: 'BOF / Converter',
      headlineId: '10009',
      textId: '10011',
      metaDesc: 'The BOF / Converter in melt shop operations. meltshop|365 supports process automation and data acquisition at the converter.',
    },
  },
  'eaf': {
    de: {
      title: 'EAF / Lichtbogenofen',
      headlineId: '10012',
      textId: '10014',
      metaDesc: 'Der Lichtbogenofen (EAF) im Schmelzbetrieb. meltshop|365 ermöglicht die digitale Prozessführung und Datenerfassung am Lichtbogenofen.',
    },
    en: {
      title: 'EAF / Electric Arc Furnace',
      headlineId: '10013',
      textId: '10015',
      metaDesc: 'The Electric Arc Furnace (EAF) in melt shop operations. meltshop|365 enables digital process control and data acquisition at the electric arc furnace.',
    },
  },
  'induktionsofen': {
    de: {
      title: 'Induktionsofen',
      headlineId: '10016',
      textId: '10018',
      metaDesc: 'Der Induktionsofen im Schmelzbetrieb. meltshop|365 bietet umfassende Funktionen zur Steuerung und Überwachung des Induktionsofens.',
    },
    en: {
      title: 'Induction Furnace',
      headlineId: '10017',
      textId: '10019',
      metaDesc: 'The induction furnace in melt shop operations. meltshop|365 offers comprehensive functions for controlling and monitoring the induction furnace.',
    },
  },
  'vakuumanlage': {
    de: {
      title: 'Vakuumanlage / RH Anlage',
      headlineId: '10020',
      textId: '10022',
      metaDesc: 'Die Vakuumanlage / RH Anlage im Schmelzbetrieb. meltshop|365 bietet Funktionen zur Steuerung und Überwachung der Vakuumbehandlung.',
    },
    en: {
      title: 'Vacuum Plant / RH Plant',
      headlineId: '10021',
      textId: '10023',
      metaDesc: 'The vacuum plant / RH plant in melt shop operations. meltshop|365 offers functions for controlling and monitoring vacuum treatment.',
    },
  },
  'pfannenofen': {
    de: {
      title: 'Pfannenofen',
      headlineId: '10024',
      textId: '10026',
      metaDesc: 'Der Pfannenofen im Schmelzbetrieb. meltshop|365 unterstützt die digitale Prozessführung am Pfannenofen.',
    },
    en: {
      title: 'Ladle Furnace',
      headlineId: '10025',
      textId: '10027',
      metaDesc: 'The ladle furnace in melt shop operations. meltshop|365 supports digital process control at the ladle furnace.',
    },
  },
  'stranggussanlage': {
    de: {
      title: 'Stranggussanlage',
      headlineId: '10028',
      textId: '10030',
      metaDesc: 'Die Stranggussanlage im Schmelzbetrieb. meltshop|365 bietet umfassende Funktionen zur Steuerung und Überwachung des Stranggießprozesses.',
    },
    en: {
      title: 'Continuous Casting',
      headlineId: '10029',
      textId: '10031',
      metaDesc: 'Continuous casting in melt shop operations. meltshop|365 offers comprehensive functions for controlling and monitoring the continuous casting process.',
    },
  },
  'blockguss': {
    de: {
      title: 'Blockguss / Kokillenguss',
      headlineId: '10032',
      textId: '10034',
      metaDesc: 'Blockguss / Kokillenguss im Schmelzbetrieb. meltshop|365 unterstützt die digitale Prozessführung beim Blockguss.',
    },
    en: {
      title: 'Block Casting / Ingot Casting',
      headlineId: '10033',
      textId: '10035',
      metaDesc: 'Block casting / Ingot casting in melt shop operations. meltshop|365 supports digital process control for block casting.',
    },
  },
  'spuelstand': {
    de: {
      title: 'Spülstand',
      headlineId: '10036',
      textId: '10038',
      metaDesc: 'Der Spülstand im Schmelzbetrieb. meltshop|365 unterstützt die digitale Prozessführung und Überwachung am Spülstand.',
    },
    en: {
      title: 'Purging Stand',
      headlineId: '10037',
      textId: '10039',
      metaDesc: 'The purging stand in melt shop operations. meltshop|365 supports digital process control and monitoring at the purging stand.',
    },
  },
};

const AnlagePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language } = React.useContext(LanguageContext);

  const config = slug ? plantConfigs[slug] : undefined;
  const IconComponent = slug ? plantIconMap[slug] : undefined;

  if (!config) {
    return (
      <div className="d-flex flex-column min-vh-100">
        <NavigationBar />
        <div className="bg-image d-flex flex-column justify-content-center align-items-center flex-grow-1">
          <div className="bg-white p-5 rounded mx-5 my-5">
            <h1 className="h1-grips">
              {language === 'de' ? 'Anlage nicht gefunden' : 'Plant not found'}
            </h1>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const lang = language === 'de' ? config.de : config.en;
  const keywords = 'ERP, Industrie 4.0, Schmelz- und Gießbetrieb, Software, ERP-Software, Cloud';

  return (
    <div className="d-flex flex-column min-vh-100">
      <Helmet>
        <title>{lang.title} - meltshop365</title>
        <meta name="description" content={lang.metaDesc} />
        <meta name="keywords" content={keywords} />
      </Helmet>

      <NavigationBar />

      <div className="bg-image d-flex flex-column justify-content-center align-items-center flex-grow-1">
        <div className="bg-white p-5 rounded mx-5 my-5 w-75">
          {/* Page Title (editable — same ID as SVG tooltip text) */}
          <h1 className="my-4 mt-5 h1-grips">
            <EditableText id={lang.headlineId} isH1 />
          </h1>
          <div className="my-4" style={{ width: '100px', borderBottom: '10px solid red' }}></div>

          {/* Two-column layout: Icon left, text right */}
          <div className="row align-items-start">
            <div className="col-md-4 d-flex justify-content-center mb-4 mb-md-0">
              {IconComponent && <IconComponent />}
            </div>
            <div className="col-md-8">
              <div className="mt-3">
                <EditableText id={lang.textId} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AnlagePage;
